/**
 * useTasks — fetches real FHIR Task resources from Medplum.
 *
 * Replaces the previous `useState(mockTasks)` pattern.
 * Provides loading / error / refresh lifecycle (read-only inbox, ENG-3764 D29).
 */

import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import type { Task } from '@medplum/fhirtypes';
import { useCallback, useEffect, useState } from 'react';
import { enrichTasksWithPatientDisplay } from '../../../lib/enrichTasksWithPatientDisplay';
import { searchTasks } from '../services/taskService';
import { getUserFacingErrorMessage } from '@/lib/userFacingError';

export interface UseTasksResult {
  tasks: Task[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useTasks(): UseTasksResult {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const refresh = useCallback(() => setTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    (async () => {
      try {
        const results = await searchTasks(medplum);
        const enriched = await enrichTasksWithPatientDisplay(medplum, results);
        if (!cancelled) setTasks(enriched);
      } catch (err) {
        if (!cancelled) {
          console.error('[useTasks] Failed to fetch tasks:', err);
          setError(getUserFacingErrorMessage(err, 'Failed to load tasks'));
          setTasks([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [medplum, profile?.id, profile?.resourceType, tick]);

  return {
    tasks,
    loading,
    error,
    refresh,
  };
}
